/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useManagerTasks.ts
 * PURPOSE: BR-WF-003 Manager Task Assignment ("Giao việc") — TanStack Query
 *          hooks cho roster, giao việc hàng loạt, danh sách chờ duyệt, duyệt/từ chối
 *          + ISS-MGR-12: list/create/assign/bulk-assign cho ManagerStaffPage [T3]
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-12
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Manager giao việc cho NV
 * - Impact: Revenue Critical — vận hành hàng ngày tại chi nhánh
 * - Stakeholders: Branch/Shift Managers, Employees
 *
 * @technical-architecture
 * - Layer: Presentation Support (React Hooks)
 * - Pattern: TanStack Query v5 (useQuery, useMutation, useQueryClient)
 * - Dependencies: @tanstack/react-query, managerTasksApi, managerApi
 *
 * @cross-references
 * - API: frontend/src/services/api/managerTasksApi.ts
 * - API: frontend/src/services/api/managerApi.ts (getTasks/createTask/assignTask/bulkAssignTasks)
 * - Design: docs/01-planning/01-requirements/09-workflows/BR-WF-003-manager-task-assignment.md
 * - Component: frontend/src/pages/manager/ManagerStaffPage.tsx ([T3] tab)
 * ===============================================================================
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  managerTasksApi,
  type ApproveTaskPayload,
  type BulkCreateTaskPayload,
} from "../services/api/managerTasksApi";
import {
  managerApi,
  type ManagerTaskAssignRequest,
  type ManagerTaskBulkAssignRequest,
  type ManagerTaskCreateRequest,
} from "../services/api/managerApi";

// ── Query keys ──────────────────────────────────────────────────────────────

export const MANAGER_TASKS_QUERY_KEYS = {
  all: ["manager", "tasks"] as const,
  roster: (date?: string) => ["manager", "tasks", "roster", date ?? ""] as const,
  pendingApproval: ["manager", "tasks", "pending-approval"] as const,
  summaryByEmployee: (dateFrom?: string, dateTo?: string) =>
    ["manager", "tasks", "summary-by-employee", dateFrom ?? "", dateTo ?? ""] as const,
  history: (dateFrom?: string, dateTo?: string) =>
    ["manager", "tasks", "history", dateFrom ?? "", dateTo ?? ""] as const,
};

export const MANAGER_TASK_QUERY_KEYS = {
  list: ["manager", "tasks"] as const,
};

// ── BR-WF-003: Roster + Bulk create + Approve (managerTasksApi) ─────────────

/** Roster NV cho modal Giao việc — nhóm theo khu vực + có ca hôm nay */
export function useTaskRoster(date?: string) {
  return useQuery({
    queryKey: MANAGER_TASKS_QUERY_KEYS.roster(date),
    queryFn: () => managerTasksApi.getRoster(date),
    staleTime: 30_000,
    retry: 2,
  });
}

/** Danh sách nhiệm vụ Đã xong đang chờ duyệt */
export function usePendingApprovalTasks() {
  return useQuery({
    queryKey: MANAGER_TASKS_QUERY_KEYS.pendingApproval,
    queryFn: () => managerTasksApi.getPendingApproval(),
    staleTime: 10_000,
    retry: 2,
  });
}

function useInvalidateTasks() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: MANAGER_TASKS_QUERY_KEYS.all });
}

/** Modal Giao việc: gửi — tạo 1 nhiệm vụ độc lập cho mỗi NV được chọn */
export function useBulkCreateTasks() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (payload: BulkCreateTaskPayload) => managerTasksApi.bulkCreate(payload),
    onSuccess: invalidate,
  });
}

/** Duyệt / Từ chối 1 nhiệm vụ đang chờ duyệt */
export function useApproveTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: ApproveTaskPayload }) =>
      managerTasksApi.approve(taskId, payload),
    onSuccess: invalidate,
  });
}

/** Duyệt nhiều nhiệm vụ cùng lúc — gọi lại API duyệt-1-cái cho từng ID đã chọn
 * (backend chưa có endpoint bulk-approve riêng, không cần thêm cho quy mô này). */
export function useBulkApproveTasks() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (taskIds: string[]) =>
      Promise.all(taskIds.map((taskId) => managerTasksApi.approve(taskId, { approved: true }))),
    onSuccess: invalidate,
  });
}

/** Tổng hợp nhiệm vụ theo NV (ai làm nhiều/ít) — trang báo cáo cuối tháng */
export function useEmployeeTaskSummary(dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: MANAGER_TASKS_QUERY_KEYS.summaryByEmployee(dateFrom, dateTo),
    queryFn: () => managerTasksApi.getSummaryByEmployee({ date_from: dateFrom, date_to: dateTo }),
    staleTime: 30_000,
    retry: 2,
  });
}

/** Lịch sử nhiệm vụ (2 phần: chưa hoàn thành / đã hoàn thành) trong khoảng ngày */
export function useTaskHistory(dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: MANAGER_TASKS_QUERY_KEYS.history(dateFrom, dateTo),
    queryFn: () => managerTasksApi.getHistory({ date_from: dateFrom, date_to: dateTo, limit: 100 }),
    staleTime: 30_000,
    retry: 2,
  });
}

// ── ISS-MGR-12: List + Create + Assign (managerApi) ─────────────────────────

/** [T3] danh sách việc — branch-scoped (server-side, apply_branch_scope_filter). */
export function useManagerTasksList() {
  return useQuery({
    queryKey: MANAGER_TASK_QUERY_KEYS.list,
    queryFn: () => managerApi.getTasks(),
    staleTime: 15_000,
    retry: 2,
  });
}

/** [K-FORM] tạo + giao việc 1 bước (assigned_to_id tuỳ chọn ở creation). */
export function useCreateManagerTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ManagerTaskCreateRequest) => managerApi.createTask(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANAGER_TASK_QUERY_KEYS.list });
    },
  });
}

/** [K3] ⋮ "Giao lại" — reassign 1 việc đã có cho người khác. */
export function useAssignManagerTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: ManagerTaskAssignRequest }) =>
      managerApi.assignTask(taskId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANAGER_TASK_QUERY_KEYS.list });
    },
  });
}

/** [K3] bulk bar — "Chuyển N việc cho 1 nhân viên" (reassignment, không phải fan-out). */
export function useBulkAssignManagerTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ManagerTaskBulkAssignRequest) => managerApi.bulkAssignTasks(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANAGER_TASK_QUERY_KEYS.list });
    },
  });
}
